/**
 * @fileoverview "Updating your garden..." over the advice (owner, 2026-10-16):
 * a slim, calm note with the shared loader's motion at the top of the jobs
 * while a fresh brief or outlook is on its way and the old advice is showing,
 * and the old advice looking gently quieter until the new arrives.
 *
 * - GuidanceUpdating: the note (GrowLoader, inline). It goes when the update
 *   is over. It floats over the top of the quieted advice and takes no room,
 *   so nothing on the page moves when it comes or goes, and a tap passes
 *   through it (the audit, 2026-10-16: in the flow it pushed the page down
 *   0.6 s after a read began, and a tap landed on the wrong button). If the
 *   update failed on our side, it is the standard sentence (D31: reported,
 *   with the support address, no "try again") in its place, in the flow,
 *   never a loader that stays.
 * - GuidanceQuiet: wraps the old advice; it fades to 60% and says it is busy
 *   (aria-busy) while the update lasts. Under prefers-reduced-motion the fade
 *   is a still change.
 *
 * Where: Today (above the jobs), the top of Plants, the Beds page, Weather.
 * Words: loader.updating (en/es).
 *
 * @module GuidanceUpdating
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { GrowLoader } from '@/shared/components/ui/GrowLoader';
import { ServerFailureNote } from '@/shared/components/ServerFailure';
import { useGuidanceUpdating } from '@/shared/hooks/useGuidanceUpdating';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { isServerFailure } from '@/shared/lib/serverFailure';

/** What GuidanceUpdating is given. */
interface GuidanceUpdatingProps {
  /** Classes for the failure sentence in the flow (the floating note takes none). */
  className?: string;
  /** Where the failure is reported from, for Sentry ("Today", "Plants"...). */
  where?: string;
}

/**
 * The "Updating your garden..." note; nothing when nothing is updating.
 *
 * @param props - GuidanceUpdatingProps.
 */
export const GuidanceUpdating: React.FC<GuidanceUpdatingProps> = ({ className, where = 'Guidance update' }) => {
  const { reactTranslate: t } = useTranslation();
  const { updating, failure } = useGuidanceUpdating();
  if (failure !== null && isServerFailure(failure)) {
    return <ServerFailureNote error={failure} where={where} className={className} />;
  }
  if (!updating) return null;
  // No height of its own: the note sits over the advice below, which is quieter while it shows.
  return (
    // !mt-0: in a spaced stack (Today's space-y-4) it adds no gap either.
    <div className="relative z-10 h-0 !mt-0">
      {/* The page's own ground under the note's light tint, so the advice it floats over does not show through. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 rounded-lg bg-background shadow-sm">
        <GrowLoader variant="inline" label={t('loader.updating')} />
      </div>
    </div>
  );
};

/** What GuidanceQuiet is given. */
interface GuidanceQuietProps {
  /** The old advice, drawn as it always is. */
  children: React.ReactNode;
  /** Classes for the wrapper. */
  className?: string;
}

/**
 * The old advice, a little quieter while it is being updated.
 *
 * @param props - GuidanceQuietProps.
 */
export const GuidanceQuiet: React.FC<GuidanceQuietProps> = ({ children, className }) => {
  const { updating } = useGuidanceUpdating();
  return (
    <div
      aria-busy={updating || undefined}
      className={cn('transition-opacity duration-500 motion-reduce:transition-none', updating && 'opacity-60', className)}
    >
      {children}
    </div>
  );
};
